import React, { useState, useMemo, useEffect } from 'react';
import { jsPDF } from 'jspdf';
import './ProductComparisonDashboard.css';

const ProductComparisonDashboard = ({ products, onClose }) => {
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("all");
  const [activeTab, setActiveTab] = useState("overview");
  const [expandedProduct, setExpandedProduct] = useState(null);

  // Debug: Log the received products data
  useEffect(() => {
    console.log('ProductComparisonDashboard received products:', products);
    console.log('Products keys:', products ? Object.keys(products) : 'No products');
  }, [products]);

  // Scroll to top when component mounts
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // Extract product data from the AI-generated comparison data
  const productData = useMemo(() => {
    if (!products.overview?.product_overview) return [];
    return products.overview.product_overview.map((product, index) => ({
      id: index + 1,
      name: product.product,
      brand: product.brand_and_type?.split(' • ')[0] || 'Unknown Brand',
      category: product.brand_and_type?.split(' • ')[1] || 'General',
      price: product.price?.value || 0,
      packaging: product.price?.quantity || '60 gummies',
      rating: product.rating?.value || 0,
      totalRatings: product.rating?.reviews ? parseInt(product.rating.reviews) : 0,
      marketPosition: product.position || 'Budget',
      targetAudience: product.target || 'General',
      keyIngredients: [], // Will be populated from detailed_comparison
      uniqueFeatures: [], // Will be populated from detailed_comparison
      advantages: [], // Will be populated from detailed_comparison
      disadvantages: [] // Will be populated from detailed_comparison
    }));
  }, [products]);

  // Populate additional data from detailed_comparison
  const enrichedProductData = useMemo(() => {
    if (!products.detailed_comparison?.product_analysis) return productData;

    return productData.map(product => {
      const detailedProduct = products.detailed_comparison.product_analysis.find(
        p => p.product === product.name
      );

      if (detailedProduct) {
        return {
          ...product,
          advantages: detailedProduct.competitive_advantages || [],
          disadvantages: detailedProduct.areas_for_improvement || []
        };
      }

      return product;
    });
  }, [productData, products]);

  const filteredProducts = useMemo(() => {
    return enrichedProductData.filter((product) => {
      const matchesSearch =
        product.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        product.brand.toLowerCase().includes(searchTerm.toLowerCase());
      const matchesCategory = selectedCategory === "all" || product.category === selectedCategory;
      return matchesSearch && matchesCategory;
    });
  }, [enrichedProductData, searchTerm, selectedCategory]);

  const categories = useMemo(() => {
    return Array.from(new Set(enrichedProductData.map((p) => p.category)));
  }, [enrichedProductData]);

  const getPositionColor = (position) => {
    switch (position) {
      case "Premium":
        return "bg-purple-500/10 text-purple-400 border-purple-500/20";
      case "Mid-range":
        return "bg-blue-500/10 text-blue-400 border-blue-500/20";
      case "Budget":
        return "bg-green-500/10 text-green-400 border-green-500/20";
      default:
        return "bg-gray-500/10 text-gray-400 border-gray-500/20";
    }
  };

  const renderOverview = () => {
    // Use AI-generated overview stats directly
    const overview = products.overview || {};
    const avgPrice = overview.avg_price?.value || 0;
    const priceRange = overview.avg_price?.range || '₹0 - ₹0';
    const avgRating = overview.avg_rating?.value || 0;
    const premiumCount = overview.market_leaders?.value || 0;
    const totalProducts = overview.product_overview?.length || 0;

    return (
      <div className="pro-dash-space-y-6">
        {/* Stats Cards */}
        <div className="pro-dash-stats-grid">
          <div className="pro-dash-stat-card">
            <h3 className="pro-dash-stat-title">Avg Price</h3>
            <div className="pro-dash-stat-value">₹{avgPrice}</div>
            <p className="pro-dash-stat-subtitle">{priceRange}</p>
          </div>

          <div className="pro-dash-stat-card">
            <h3 className="pro-dash-stat-title">Avg Rating</h3>
            <div className="pro-dash-stat-value">{avgRating}</div>
            <p className="pro-dash-stat-subtitle">Based on reviews</p>
          </div>

          <div className="pro-dash-stat-card">
            <h3 className="pro-dash-stat-title">Premium Products</h3>
            <div className="pro-dash-stat-value">{premiumCount}</div>
            <p className="pro-dash-stat-subtitle">{overview.market_leaders?.description || 'High-end positioning'}</p>
          </div>

          <div className="pro-dash-stat-card">
            <h3 className="pro-dash-stat-title">Total Products</h3>
            <div className="pro-dash-stat-value">{totalProducts}</div>
            <p className="pro-dash-stat-subtitle">In comparison</p>
          </div>
        </div>

      {/* Product Table */}
      <div className="pro-dash-product-table-container">
        <div className="pro-dash-product-table-header">
          <h3 className="pro-dash-product-table-title">Product Overview</h3>
        </div>
        <div className="pro-dash-table-wrapper">
          <table className="pro-dash-product-table">
            <thead>
              <tr className="pro-dash-table-header">
                <th className="pro-dash-table-header-cell">Product</th>
                <th className="pro-dash-table-header-cell">Price</th>
                <th className="pro-dash-table-header-cell">Rating</th>
                <th className="pro-dash-table-header-cell">Position</th>
                <th className="pro-dash-table-header-cell">Target</th>
                {/* <th className="pro-dash-table-header-cell">Actions</th> */}
              </tr>
            </thead>
            <tbody>
              {filteredProducts.map((product) => (
                <tr key={product.id} className="pro-dash-table-row">
                  <td className="pro-dash-table-cell">
                    <div>
                      <div className="pro-dash-product-name">{product.name}</div>
                      <div className="pro-dash-product-meta">
                        {product.brand} • {product.category}
                      </div>
                    </div>
                  </td>
                  <td className="pro-dash-table-cell">
                    <div className="pro-dash-product-price">₹{product.price}</div>
                    <div className="pro-dash-product-meta">{product.packaging}</div>
                  </td>
                  <td className="pro-dash-table-cell">
                    <div className="pro-dash-rating-container">
                      <span className="pro-dash-star-icon">★</span>
                      <span className="pro-dash-rating-text">{product.rating || "N/A"}</span>
                    </div>
                    <div className="pro-dash-product-meta">
                      {product.totalRatings > 0 ? `${product.totalRatings} reviews` : "New product"}
                    </div>
                  </td>
                  <td className="pro-dash-table-cell">
                    <span className={`position-badge ${getPositionColor(product.marketPosition)}`}>
                      {product.marketPosition}
                    </span>
                  </td>
                  <td className="pro-dash-table-cell">
                    <div className="pro-dash-target-audience">{product.targetAudience}</div>
                  </td>
                  {/* <td className="pro-dash-table-cell">
                    <button
                      onClick={() => setExpandedProduct(expandedProduct === product.id ? null : product.id)}
                      className="pro-dash-expand-btn"
                    >
                      👁️
                    </button>
                  </td> */}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Expanded Product Details */}
      {expandedProduct && (
        <div className="pro-dash-expanded-details">
          {(() => {
            const product = enrichedProductData.find((p) => p.id === expandedProduct);
            if (!product) return null;

            return (
              <div className="pro-dash-details-grid">
                <div>
                  <h4 className="pro-dash-details-title">Key Ingredients</h4>
                  <div className="pro-dash-ingredients-list">
                    {product.keyIngredients.map((ingredient, idx) => (
                      <span key={idx} className="pro-dash-ingredient-tag">
                        {ingredient}
                      </span>
                    ))}
                  </div>
                </div>

                <div>
                  <h4 className="pro-dash-details-title">Unique Features</h4>
                  <div className="pro-dash-features-list">
                    {product.uniqueFeatures.map((feature, idx) => (
                      <div key={idx} className="pro-dash-feature-item">
                        <div className="pro-dash-feature-dot"></div>
                        {feature}
                      </div>
                    ))}
                  </div>
                </div>

                <div>
                  <h4 className="pro-dash-details-title">Competitive Analysis</h4>
                  <div className="pro-dash-analysis-list">
                    <div>
                      <div className="pro-dash-analysis-label">Advantages</div>
                      {product.advantages.map((adv, idx) => (
                        <div key={idx} className="pro-dash-advantage-item">
                          <span className="pro-dash-advantage-icon">↗</span>
                          {adv}
                        </div>
                      ))}
                    </div>
                    <div>
                      <div className="pro-dash-analysis-label">Disadvantages</div>
                      {product.disadvantages.map((dis, idx) => (
                        <div key={idx} className="pro-dash-disadvantage-item">
                          <span className="pro-dash-disadvantage-icon">↘</span>
                          {dis}
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            );
          })()}
        </div>
      )}
    </div>
  );
};

  const renderComparison = () => {
    // Use AI-generated comparison data directly
    const featureComparison = products.detailed_comparison?.feature_comparison_matrix || {};
    const ingredientComparison = products.detailed_comparison?.ingredient_comparison || {};

    return (
      <div className="pro-dash-space-y-6">
        {/* Feature Comparison Matrix */}
        <div className="pro-dash-comparison-card">
          <div className="pro-dash-card-header">
            <h3 className="pro-dash-card-title">Feature Comparison Matrix</h3>
            <p className="pro-dash-card-description">{featureComparison.description || 'Compare key features across all products'}</p>
          </div>
          <div className="pro-dash-card-content">
            <div className="pro-dash-table-wrapper">
              <table className="pro-dash-comparison-table">
                <thead>
                  <tr className="pro-dash-table-header">
                    <th className="pro-dash-table-header-cell-left">Feature</th>
                    {enrichedProductData.map((product) => (
                      <th key={product.id} className="pro-dash-table-header-cell-center">
                        <div className="pro-dash-brand-name">{product.brand}</div>
                        <div className="pro-dash-category-name">{product.category}</div>
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {(featureComparison.features || []).slice(0, 10).map((feature, idx) => (
                    <tr key={idx} className="pro-dash-table-row">
                      <td className="pro-dash-table-cell-left">{feature.name}</td>
                      {enrichedProductData.map((product) => {
                        // Try both key formats: brand name first, then "BRAND CATEGORY"
                        const brandKey = product.brand;
                        const brandCategoryKey = `${product.brand} ${product.category}`;
                        const hasFeature = feature[brandKey] !== undefined ? feature[brandKey] : feature[brandCategoryKey];
                        return (
                          <td key={product.id} className="pro-dash-table-cell-center">
                            {hasFeature ? (
                              <span className="pro-dash-check-icon">✓</span>
                            ) : (
                              <span className="pro-dash-cross-icon">✗</span>
                            )}
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>

        {/* Ingredient Comparison */}
        <div className="pro-dash-comparison-card">
          <div className="pro-dash-card-header">
            <h3 className="pro-dash-card-title">Ingredient Comparison</h3>
            <p className="pro-dash-card-description">{ingredientComparison.description || 'Key ingredients across products'}</p>
          </div>
          <div className="pro-dash-card-content">
            <div className="pro-dash-table-wrapper">
              <table className="pro-dash-comparison-table">
                <thead>
                  <tr className="pro-dash-table-header">
                    <th className="pro-dash-table-header-cell-left">Ingredient</th>
                    {enrichedProductData.map((product) => (
                      <th key={product.id} className="pro-dash-table-header-cell-center">
                        <div className="pro-dash-brand-name">{product.brand}</div>
                      </th>
                    ))}
                    <th className="pro-dash-table-header-cell-center">Coverage</th>
                  </tr>
                </thead>
                <tbody>
                  {(ingredientComparison.ingredients || []).slice(0, 15).map((ingredient, idx) => (
                    <tr key={idx} className="pro-dash-table-row">
                      <td className="pro-dash-table-cell-left">{ingredient.name}</td>
                      {enrichedProductData.map((product) => {
                        // For ingredients, AI uses just brand name as key
                        const brandKey = product.brand;
                        return (
                          <td key={product.id} className="pro-dash-table-cell-center">
                            {ingredient[brandKey] ? (
                              <span className="pro-dash-check-icon">✓</span>
                            ) : (
                              <span className="pro-dash-cross-icon">✗</span>
                            )}
                          </td>
                        );
                      })}
                      <td className="pro-dash-table-cell-center">
                        <span className={`coverage-badge ${
                          parseInt(ingredient.coverage) >= 70
                            ? "coverage-high"
                            : parseInt(ingredient.coverage) >= 40
                              ? "coverage-medium"
                              : "coverage-low"
                        }`}>
                          {ingredient.coverage}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>

        {/* Competitive Positioning */}
        <div className="pro-dash-positioning-grid">
          {enrichedProductData.map((product) => (
            <div key={product.id} className="pro-dash-positioning-card">
              <div className="pro-dash-card-header">
                <h4 className="pro-dash-product-title">{product.name}</h4>
                <p className="pro-dash-product-meta">
                  {product.brand} • {product.category} • ₹{product.price}
                </p>
              </div>
              <div className="pro-dash-card-content">
                <div className="pro-dash-advantages-section">
                  <h5 className="pro-dash-section-title">Competitive Advantages</h5>
                  <div className="pro-dash-advantages-list">
                    {product.advantages.map((advantage, idx) => (
                      <div key={idx} className="pro-dash-advantage-item">
                        <span className="pro-dash-advantage-icon">↗</span>
                        <span className="pro-dash-advantage-text">{advantage}</span>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="pro-dash-disadvantages-section">
                  <h5 className="pro-dash-section-title">Areas for Improvement</h5>
                  <div className="pro-dash-disadvantages-list">
                    {product.disadvantages.map((disadvantage, idx) => (
                      <div key={idx} className="pro-dash-disadvantage-item">
                        <span className="pro-dash-disadvantage-icon">↘</span>
                        <span className="pro-dash-disadvantage-text">{disadvantage}</span>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="pro-dash-position-badge-section">
                  <span className={`position-badge ${
                    product.marketPosition === "Premium"
                      ? "position-premium"
                      : product.marketPosition === "Mid-range"
                        ? "position-mid"
                        : "position-budget"
                  }`}>
                    {product.marketPosition} Position
                  </span>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  };

  const renderInsights = () => {
    // Use AI-generated market insights data directly
    const marketInsights = products.market_insights || {};
    const commonIngredients = marketInsights.most_common_ingredients || {};
    const commonFeatures = marketInsights.most_common_features || {};
    const audienceDistribution = marketInsights.target_audience_distribution || {};
    const priceDistribution = marketInsights.price_range_distribution || {};

    return (
      <div className="pro-dash-insights-grid">
        {/* Common Ingredients */}
        <div className="pro-dash-insights-card">
          <div className="pro-dash-card-header">
            <h3 className="pro-dash-card-title">📦 Most Common Ingredients</h3>
            <p className="pro-dash-card-description">{commonIngredients.description || 'Ingredients that appear across multiple products'}</p>
          </div>
          <div className="pro-dash-card-content">
            <div className="pro-dash-insights-list">
              {(commonIngredients.ingredients || []).map((ingredient, idx) => {
                // Parse coverage like "4/4 products" to calculate percentage
                const coverageMatch = ingredient.coverage.match(/(\d+)\/(\d+)/);
                const percentage = coverageMatch
                  ? Math.round((parseInt(coverageMatch[1]) / parseInt(coverageMatch[2])) * 100)
                  : 0;

                return (
                  <div key={idx} className="pro-dash-insight-item">
                    <div className="pro-dash-insight-header">
                      <span className="pro-dash-insight-label">{ingredient.name}</span>
                      <span className="pro-dash-insight-badge">
                        {ingredient.coverage}
                      </span>
                    </div>
                    <div className="pro-dash-progress-bar">
                      <div
                        className="pro-dash-progress-fill"
                        style={{ width: `${percentage}%` }}
                      ></div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        {/* Common Features */}
        <div className="pro-dash-insights-card">
          <div className="pro-dash-card-header">
            <h3 className="pro-dash-card-title">⭐ Most Common Features</h3>
            <p className="pro-dash-card-description">{commonFeatures.description || 'Features that differentiate products in the market'}</p>
          </div>
          <div className="pro-dash-card-content">
            <div className="pro-dash-insights-list">
              {(commonFeatures.features || []).map((feature, idx) => {
                // Parse coverage like "4/4 products" to calculate percentage
                const coverageMatch = feature.coverage.match(/(\d+)\/(\d+)/);
                const percentage = coverageMatch
                  ? Math.round((parseInt(coverageMatch[1]) / parseInt(coverageMatch[2])) * 100)
                  : 0;

                return (
                  <div key={idx} className="pro-dash-insight-item">
                    <div className="pro-dash-insight-header">
                      <span className="pro-dash-insight-label">{feature.name}</span>
                      <span className="pro-dash-insight-badge">
                        {feature.coverage}
                      </span>
                    </div>
                    <div className="pro-dash-progress-bar">
                      <div
                        className="pro-dash-progress-fill"
                        style={{ width: `${percentage}%` }}
                      ></div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        {/* Target Audience Analysis */}
        <div className="pro-dash-insights-card">
          <div className="pro-dash-card-header">
            <h3 className="pro-dash-card-title">👥 Target Audience Distribution</h3>
            <p className="pro-dash-card-description">{audienceDistribution.description || 'Market segmentation by target demographics'}</p>
          </div>
          <div className="pro-dash-card-content">
            <div className="pro-dash-insights-list">
              {(audienceDistribution.audiences || []).map((audience, idx) => (
                <div key={idx} className="pro-dash-insight-item">
                  <div className="pro-dash-insight-header">
                    <span className="pro-dash-insight-label">{audience.name}</span>
                    <span className="pro-dash-insight-badge">
                      {audience.products}
                    </span>
                  </div>
                  <div className="pro-dash-progress-bar">
                    <div
                      className="pro-dash-progress-fill"
                      style={{ width: `${(parseInt(audience.products) / enrichedProductData.length) * 100}%` }}
                    ></div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Price Range Analysis */}
        <div className="pro-dash-insights-card">
          <div className="pro-dash-card-header">
            <h3 className="pro-dash-card-title">📈 Price Range Distribution</h3>
            <p className="pro-dash-card-description">{priceDistribution.description || 'Market positioning by price segments'}</p>
          </div>
          <div className="pro-dash-card-content">
            <div className="pro-dash-insights-list">
              {(priceDistribution.price_ranges || []).map((range, idx) => (
                <div key={idx} className="pro-dash-insight-item">
                  <div className="pro-dash-insight-header">
                    <span className="pro-dash-insight-label">{range.name}</span>
                    <span className="pro-dash-insight-badge">
                      {range.products}
                    </span>
                  </div>
                  <div className="pro-dash-progress-bar">
                    <div
                      className="pro-dash-progress-fill"
                      style={{ width: `${(parseInt(range.products) / enrichedProductData.length) * 100}%` }}
                    ></div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderOpportunities = () => {
    // Use AI-generated opportunities data directly
    const opportunities = products.opportunities || {};
    const marketWhiteSpaces = opportunities.market_white_spaces || {};
    const innovationOpportunities = opportunities.innovation_opportunities || {};
    const recommendedActions = opportunities.recommended_actions || [];
    const competitiveStrategy = opportunities.competitive_positioning_strategy || {};

    return (
      <div className="pro-dash-opportunities-container">
        {/* Market White Spaces */}
        <div className="pro-dash-opportunities-card">
          <div className="pro-dash-card-header">
            <h3 className="pro-dash-card-title">🎯 Market White Spaces</h3>
            <p className="pro-dash-card-description">{marketWhiteSpaces.description || 'Underserved segments and opportunities for new product development'}</p>
          </div>
          <div className="pro-dash-card-content">
            <div className="pro-dash-gaps-grid">
              {/* Audience Gap Opportunities */}
              <div className="pro-dash-gap-section">
                <h4 className="pro-dash-gap-title">Audience Gaps</h4>
                <div className="pro-dash-gap-description">
                  {(marketWhiteSpaces.audience_gaps || []).map((gap, idx) => (
                    <div key={idx} className="pro-dash-audience-gap">
                      <strong>{gap.segment}:</strong> {gap.description}
                    </div>
                  ))}
                </div>
              </div>

              {/* Feature Opportunities */}
              <div className="pro-dash-gap-section">
                <h4 className="pro-dash-gap-title">Feature Opportunities</h4>
                <div className="pro-dash-feature-list">
                  {(marketWhiteSpaces.feature_opportunities || []).map((feature, idx) => (
                    <div key={idx} className="pro-dash-feature-item">
                      <div className="pro-dash-feature-dot"></div>
                      <span className="pro-dash-feature-text">{feature}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Innovation Opportunities */}
        <div className="pro-dash-innovation-grid">
          <div className="pro-dash-opportunities-card">
            <div className="pro-dash-card-header">
              <h3 className="pro-dash-card-title">💡 Innovation Opportunities</h3>
              <p className="pro-dash-card-description">{innovationOpportunities.description || 'Emerging trends and underutilized ingredients'}</p>
            </div>
            <div className="pro-dash-card-content">
              <div>
                <h4 className="pro-dash-section-title mb-3">Underutilized Ingredients</h4>
                <div className="pro-dash-ingredients-grid">
                  {(innovationOpportunities.underutilized_ingredients || []).map((ingredient, idx) => (
                    <span key={idx} className="pro-dash-ingredient-badge">
                      {ingredient}
                    </span>
                  ))}
                </div>
              </div>

              <div className="pro-dash-mt-4">
                <h4 className="pro-dash-section-title mb-3">Trending Formats</h4>
                <div className="pro-dash-trends-list">
                  {(innovationOpportunities.trending_formats || []).map((format, idx) => (
                    <div key={idx} className="pro-dash-trend-item">
                      <span className="pro-dash-trend-icon">📈</span>
                      <span className="pro-dash-trend-text">{format}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>

          <div className="pro-dash-opportunities-card">
            <div className="pro-dash-card-header">
              <h3 className="pro-dash-card-title">✅ Recommended Actions</h3>
              <p className="pro-dash-card-description">Strategic recommendations based on market analysis</p>
            </div>
            <div className="pro-dash-card-content">
              <div className="pro-dash-actions-list">
                {recommendedActions.map((action, idx) => (
                  <div key={idx} className={`pro-dash-action-item action-${action.priority.toLowerCase().replace(' ', '-')}`}>
                    <div className="pro-dash-action-header">
                      <span className="pro-dash-action-priority">{action.priority}</span>
                      <span className="pro-dash-action-badge">{action.type}</span>
                    </div>
                    <p className="pro-dash-action-description">
                      {action.action}
                    </p>
                    {/* <button className="pro-dash-action-button">
                      View Details →
                    </button> */}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Competitive Positioning Recommendations */}
        <div className="pro-dash-opportunities-card">
          <div className="pro-dash-card-header">
            <h3 className="pro-dash-strategy-title">Competitive Positioning Strategy</h3>
            <p className="pro-dash-card-description">{competitiveStrategy.description || 'How to position your product for maximum market impact'}</p>
          </div>
          <div className="pro-dash-card-content">
            <div className="pro-dash-strategy-grid">
              <div className="pro-dash-strategy-section">
                <h4 className="pro-dash-strategy-title">Differentiation Strategy</h4>
                <div className="pro-dash-strategy-list">
                  {(competitiveStrategy.differentiation_strategy || []).map((strategy, idx) => (
                    <div key={idx} className="pro-dash-strategy-item">{strategy}</div>
                  ))}
                </div>
              </div>

              <div className="pro-dash-strategy-section">
                <h4 className="pro-dash-strategy-title">Pricing Strategy</h4>
                <div className="pro-dash-strategy-list">
                  {(competitiveStrategy.pricing_strategy || []).map((strategy, idx) => (
                    <div key={idx} className="pro-dash-strategy-item">{strategy}</div>
                  ))}
                </div>
              </div>

              <div className="pro-dash-strategy-section">
                <h4 className="pro-dash-strategy-title">Go-to-Market</h4>
                <div className="pro-dash-strategy-list">
                  {(competitiveStrategy.go_to_market || []).map((strategy, idx) => (
                    <div key={idx} className="pro-dash-strategy-item">{strategy}</div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  // PDF Download Function
  const handleDownloadPDF = () => {
    const doc = new jsPDF();

    // Title
    doc.setFontSize(20);
    doc.text('Product Intelligence Report', 20, 30);

    // Date
    doc.setFontSize(12);
    doc.text(`Generated on: ${new Date().toLocaleDateString()}`, 20, 45);

    // Overview Stats
    doc.setFontSize(16);
    doc.text('Overview', 20, 65);

    const overview = products.overview || {};
    const avgPrice = overview.avg_price?.value || 0;
    const priceRange = overview.avg_price?.range || '';
    const avgRating = overview.avg_rating?.value || 0;
    const totalProducts = enrichedProductData.length;

    doc.setFontSize(12);
    doc.text(`Average Price: ₹${avgPrice} (${priceRange})`, 20, 80);
    doc.text(`Average Rating: ${avgRating}`, 20, 90);
    doc.text(`Total Products: ${totalProducts}`, 20, 100);

    // Product List
    doc.setFontSize(14);
    doc.text('Products Compared:', 20, 120);

    enrichedProductData.forEach((product, idx) => {
      const yPos = 135 + (idx * 10);
      if (yPos > 270) return; // Prevent overflow
      doc.setFontSize(10);
      doc.text(`${product.brand} - ₹${product.price} (${product.marketPosition})`, 25, yPos);
    });

    // Market Insights
    if (doc.internal.getCurrentPageInfo().pageNumber === 1) {
      doc.addPage();
    }

    doc.setFontSize(16);
    doc.text('Market Insights', 20, 30);

    const marketInsights = products.market_insights || {};
    const commonIngredients = marketInsights.most_common_ingredients || {};
    const commonFeatures = marketInsights.most_common_features || {};

    doc.setFontSize(12);
    doc.text('Common Ingredients:', 20, 45);
    (commonIngredients.ingredients || []).slice(0, 3).forEach((ing, idx) => {
      doc.text(`• ${ing.name}: ${ing.coverage}`, 25, 55 + (idx * 8));
    });

    doc.text('Common Features:', 20, 75);
    (commonFeatures.features || []).slice(0, 3).forEach((feat, idx) => {
      doc.text(`• ${feat.name}: ${feat.coverage}`, 25, 85 + (idx * 8));
    });

    // Opportunities
    doc.addPage();
    doc.setFontSize(16);
    doc.text('Opportunities', 20, 30);

    const opportunities = products.opportunities || {};
    const recommendedActions = opportunities.recommended_actions || [];

    doc.setFontSize(12);
    doc.text('Recommended Actions:', 20, 45);
    recommendedActions.slice(0, 3).forEach((action, idx) => {
      const yPos = 55 + (idx * 15);
      doc.text(`${action.priority}: ${action.type}`, 25, yPos);
      doc.setFontSize(10);
      const lines = doc.splitTextToSize(action.action, 150);
      doc.text(lines, 25, yPos + 5);
      doc.setFontSize(12);
    });

    // Save the PDF
    doc.save(`product-comparison-${new Date().toISOString().split('T')[0]}.pdf`);
  };

  return (
    <div className="pro-dash-product-comparison-dashboard">
      <div className="pro-dash-dashboard-header">
        <div className="pro-dash-header-content">
          <div>
            <h1>Product Intelligence</h1>
            <p>Comprehensive market analysis and competitive insights</p>
          </div>
        </div>
        <div className="pro-dash-header-right">
          <div className="pro-dash-header-stats">
            <span>{enrichedProductData.length} Products</span>
            {/* <span>5 Categories</span> */}
          </div>
          {/* <button className="pro-dash-download-btn" onClick={handleDownloadPDF}>
            📄 Download PDF
          </button> */}
          <button className="pro-dash-back-btn" onClick={onClose}>
            ← Back to Products
          </button>
        </div>
      </div>

      {/* <div className="pro-dash-dashboard-controls">
        <div className="pro-dash-search-section">
          <input
            type="text"
            placeholder="Search products, brands, or ingredients..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
        <div className="pro-dash-filter-section">
          <select value={selectedCategory} onChange={(e) => setSelectedCategory(e.target.value)}>
            <option value="all">All Categories</option>
            {categories.map((cat) => (
              <option key={cat} value={cat}>{cat}</option>
            ))}
          </select>
        </div>
        <div className="pro-dash-filter-section">
          <button>Advanced Filters</button>
        </div>
      </div> */}

      <div className="pro-dash-dashboard-tabs">
        <button
          className={activeTab === 'overview' ? 'active' : ''}
          onClick={() => setActiveTab('overview')}
        >
          Overview
        </button>
        <button
          className={activeTab === 'comparison' ? 'active' : ''}
          onClick={() => setActiveTab('comparison')}
        >
          Detailed Comparison
        </button>
        <button
          className={activeTab === 'insights' ? 'active' : ''}
          onClick={() => setActiveTab('insights')}
        >
          Market Insights
        </button>
        <button
          className={activeTab === 'opportunities' ? 'active' : ''}
          onClick={() => setActiveTab('opportunities')}
        >
          Opportunities
        </button>
      </div>

      <div className="pro-dash-dashboard-content">
        {activeTab === 'overview' && renderOverview()}
        {activeTab === 'comparison' && renderComparison()}
        {activeTab === 'insights' && renderInsights()}
        {activeTab === 'opportunities' && renderOpportunities()}
      </div>
    </div>
  );
};

export default ProductComparisonDashboard;
